import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useOutlet } from '../contexts/OutletContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus,
  Edit,
  Search,
  Loader2,
  Package,
  AlertTriangle,
  TrendingDown,
  TrendingUp,
  RefreshCw,
} from 'lucide-react';

export default function Inventory() {
  const { t } = useTranslation();
  const { api, user } = useAuth();
  const { currentOutlet } = useOutlet();
  const { formatCurrency } = useCurrency();
  const [inventory, setInventory] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  
  const [itemForm, setItemForm] = useState({
    name: '',
    unit: 'kg',
    current_stock: 0,
    minimum_stock: 0,
    cost_per_unit: 0,
  });
  
  const [adjustmentForm, setAdjustmentForm] = useState({
    quantity_change: 0,
    reason: '',
  });

  const fetchData = useCallback(async () => {
    if (!currentOutlet?.id) return;
    setLoading(true);
    try {
      const [invResp, lowStockResp] = await Promise.all([
        api.get('/inventory', { params: { outlet_id: currentOutlet.id } }),
        api.get('/inventory/low-stock', { params: { outlet_id: currentOutlet.id } }),
      ]);
      setInventory(invResp.data);
      setLowStockItems(lowStockResp.data);
    } catch (error) {
      toast.error('Failed to fetch inventory');
    } finally {
      setLoading(false);
    }
  }, [api, currentOutlet?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openAddDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setItemForm({
        name: item.name,
        unit: item.unit,
        current_stock: item.current_stock,
        minimum_stock: item.minimum_stock,
        cost_per_unit: item.cost_per_unit,
      });
    } else {
      setEditingItem(null);
      setItemForm({
        name: '',
        unit: 'kg',
        current_stock: 0,
        minimum_stock: 0,
        cost_per_unit: 0,
      });
    }
    setShowAddDialog(true);
  };

  const handleSaveItem = async () => {
    try {
      if (editingItem) {
        await api.put(`/inventory/${editingItem.id}`, itemForm);
        toast.success('Item updated');
      } else {
        await api.post('/inventory', {
          ...itemForm,
          outlet_id: currentOutlet.id,
        });
        toast.success('Item added');
      }
      setShowAddDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save item');
    }
  };

  const openAdjustDialog = (item) => {
    setSelectedItem(item);
    setAdjustmentForm({ quantity_change: 0, reason: '' });
    setShowAdjustDialog(true);
  };

  const handleAdjustStock = async () => {
    if (!selectedItem || adjustmentForm.quantity_change === 0) return;
    try {
      await api.post(`/inventory/${selectedItem.id}/adjust`, null, {
        params: {
          quantity_change: adjustmentForm.quantity_change,
          reason: adjustmentForm.reason || 'Manual adjustment',
        },
      });
      toast.success('Stock adjusted successfully');
      setShowAdjustDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to adjust stock');
    }
  };

  const getStockLevel = (item) => {
    const percentage = (item.current_stock / (item.minimum_stock * 3)) * 100;
    return Math.min(100, Math.max(0, percentage));
  };

  const getStockColor = (item) => {
    if (item.current_stock <= item.minimum_stock) return 'bg-red-500';
    if (item.current_stock <= item.minimum_stock * 1.5) return 'bg-amber-500';
    return 'bg-green-500';
  };

  const filteredInventory = inventory.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const canManage = ['OWNER', 'ADMIN', 'MANAGER'].includes(user?.role);

  if (!currentOutlet) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <p className="text-muted-foreground">{t('inventory.select_outlet_first')}</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('inventory.title')}</h1>
          <p className="text-muted-foreground">{t('inventory.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </Button>
          {canManage && (
            <Button onClick={() => openAddDialog()} data-testid="add-inventory-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t('inventory.add_item')}
            </Button>
          )}
        </div>
      </div>

      {/* Low Stock Alert */}
      {lowStockItems.length > 0 && (
        <Card className="border-amber-500 bg-amber-500/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-5 w-5" />
              {t('inventory.low_stock_alert')} ({lowStockItems.length} {lowStockItems.length === 1 ? 'item' : t('dashboard.items')})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {lowStockItems.map((item) => (
                <Badge key={item.id} variant="outline" className="bg-amber-500/20">
                  {item.name}: {item.current_stock} {item.unit}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t('inventory.search_placeholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="inventory-search"
        />
      </div>

      {/* Inventory Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filteredInventory.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64">
            <Package className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{t('inventory.no_items')}</p>
            {canManage && (
              <Button className="mt-4" onClick={() => openAddDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                {t('inventory.add_first_item')}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredInventory.map((item) => {
            const isLowStock = item.current_stock <= item.minimum_stock;
            return (
              <Card key={item.id} className={isLowStock ? 'border-amber-500' : ''} data-testid={`inventory-item-${item.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold">{item.name}</h3>
                      <p className="text-sm text-muted-foreground">{item.unit}</p>
                    </div>
                    {isLowStock && (
                      <Badge variant="destructive" className="shrink-0">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        {t('inventory.low')}
                      </Badge>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('inventory.current_stock')}</span>
                      <span className="font-mono font-semibold">
                        {item.current_stock} {item.unit}
                      </span>
                    </div>
                    <Progress value={getStockLevel(item)} className={`h-2 ${getStockColor(item)}`} />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{t('inventory.min')}: {item.minimum_stock}</span>
                      <span>{t('inventory.cost')}: {formatCurrency(item.cost_per_unit)}/{item.unit}</span>
                    </div>
                  </div>

                  {canManage && (
                    <div className="flex gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => openAdjustDialog(item)}
                        data-testid={`adjust-${item.id}`}
                      >
                        <TrendingUp className="h-3 w-3 mr-1" />
                        {t('inventory.adjust')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openAddDialog(item)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingItem ? t('inventory.edit_item') : t('inventory.add_item')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('inventory.name')} *</Label>
              <Input
                value={itemForm.name}
                onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                placeholder={t('inventory.name_placeholder')}
                data-testid="inventory-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('inventory.unit')}</Label>
                <Select
                  value={itemForm.unit}
                  onValueChange={(val) => setItemForm({ ...itemForm, unit: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">{t('inventory.units.kg')}</SelectItem>
                    <SelectItem value="g">{t('inventory.units.g')}</SelectItem>
                    <SelectItem value="l">{t('inventory.units.l')}</SelectItem>
                    <SelectItem value="ml">{t('inventory.units.ml')}</SelectItem>
                    <SelectItem value="pcs">{t('inventory.units.pcs')}</SelectItem>
                    <SelectItem value="pack">{t('inventory.units.pack')}</SelectItem>
                    <SelectItem value="box">{t('inventory.units.box')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('inventory.cost_per_unit')}</Label>
                <Input
                  type="number"
                  value={itemForm.cost_per_unit}
                  onChange={(e) => setItemForm({ ...itemForm, cost_per_unit: parseFloat(e.target.value) || 0 })}
                  data-testid="inventory-cost-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('inventory.current_stock')}</Label>
                <Input
                  type="number"
                  value={itemForm.current_stock}
                  onChange={(e) => setItemForm({ ...itemForm, current_stock: parseFloat(e.target.value) || 0 })}
                  data-testid="inventory-stock-input"
                />
              </div>
              <div>
                <Label>{t('inventory.minimum_stock')}</Label>
                <Input
                  type="number"
                  value={itemForm.minimum_stock}
                  onChange={(e) => setItemForm({ ...itemForm, minimum_stock: parseFloat(e.target.value) || 0 })}
                  data-testid="inventory-min-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSaveItem} data-testid="save-inventory-btn">{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust Stock Dialog */}
      <Dialog open={showAdjustDialog} onOpenChange={setShowAdjustDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('inventory.adjust_stock')}: {selectedItem?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-muted rounded-md text-center">
              <p className="text-sm text-muted-foreground">{t('inventory.current_stock')}</p>
              <p className="text-2xl font-bold font-mono">
                {selectedItem?.current_stock} {selectedItem?.unit}
              </p>
            </div>
            <div>
              <Label>{t('inventory.quantity_change')}</Label>
              <div className="flex gap-2 mt-1">
                <Button
                  variant="outline"
                  onClick={() => setAdjustmentForm({ ...adjustmentForm, quantity_change: adjustmentForm.quantity_change - 1 })}
                >
                  <TrendingDown className="h-4 w-4 mr-1" />
                  -1
                </Button>
                <Input
                  type="number"
                  value={adjustmentForm.quantity_change}
                  onChange={(e) => setAdjustmentForm({ ...adjustmentForm, quantity_change: parseFloat(e.target.value) || 0 })}
                  className="text-center font-mono"
                  data-testid="adjust-quantity-input"
                />
                <Button
                  variant="outline"
                  onClick={() => setAdjustmentForm({ ...adjustmentForm, quantity_change: adjustmentForm.quantity_change + 1 })}
                >
                  <TrendingUp className="h-4 w-4 mr-1" />
                  +1
                </Button>
              </div>
              <p className={`text-sm mt-1 ${adjustmentForm.quantity_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {t('inventory.new_stock')}: {(selectedItem?.current_stock || 0) + adjustmentForm.quantity_change} {selectedItem?.unit}
              </p>
            </div>
            <div>
              <Label>{t('inventory.reason')}</Label>
              <Input
                value={adjustmentForm.reason}
                onChange={(e) => setAdjustmentForm({ ...adjustmentForm, reason: e.target.value })}
                placeholder={t('inventory.reason_placeholder')}
                data-testid="adjust-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdjustDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleAdjustStock} disabled={adjustmentForm.quantity_change === 0} data-testid="confirm-adjust-btn">
              {t('inventory.confirm_adjustment')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
